import { showErrorToast } from '@/composables/useMutation';
import {
    destroy as destroyRoute,
    index as indexNotificationsRoute,
    markAllAsRead as markAllAsReadRoute,
    markAsRead as markAsReadRoute,
    recent as recentNotificationsRoute,
} from '@/routes/notifications';
import { Notification } from '@/types';
import { http, HttpNetworkError } from '@inertiajs/core';
import { router, usePage } from '@inertiajs/vue3';
import { trans, transChoice } from 'laravel-vue-i18n';
import { defineStore } from 'pinia';

/** Sans réponse du serveur : message réseau ; sinon échec générique. */
function showMarkError(error: unknown): void {
    showErrorToast(
        trans(
            error instanceof HttpNetworkError
                ? 'common.errors.network'
                : 'common.errors.mutation_failed',
        ),
    );
}

/**
 * Appel JSON des points d'entrée de la cloche, par le client HTTP d'Inertia
 * (celui de `useHttp`, jeton XSRF compris) : utilisable hors composant.
 * Une réponse ≥ 400 lève `HttpResponseError`, une coupure `HttpNetworkError`.
 */
async function json<T>(method: 'get' | 'post' | 'delete', url: string) {
    const response = await http.getClient().request({
        method,
        url,
        headers: { Accept: 'application/json' },
    });
    return JSON.parse(response.data || 'null') as T;
}

/** Marquages « lu » en cours, par notification : un double clic ou deux appelants partagent la même requête. */
const pendingReads = new Map<string, Promise<boolean>>();

/**
 * Panneau de la cloche (#271, maquette 37a) et client unique des mutations
 * (cloche et page des notifications) : marquer comme lu, tout marquer,
 * supprimer. Le panneau montre les 10 dernières notifications, lues comprises
 * (`notifications.recent`) : marquer comme lu met `read_at` à jour sans
 * retirer la ligne. Une mutation renvoie `true` en cas de succès ; un échec
 * affiche un toast d'erreur et renvoie `false`, l'état reste inchangé.
 */
export const useNotificationStore = defineStore('notifications', {
    state: () => ({
        lastFetchedAt: null as Date | null,
        unreadCount: 0,
        notifications: [] as Notification[],
        loading: false,
        /** Dernier chargement en échec (le panneau propose « Réessayer »). */
        error: false,
        /** Texte de la région `status` de la cloche (arrivées). */
        announcement: '',
        /** Incrémenté à chaque annonce : un texte identique est annoncé de nouveau. */
        announcementKey: 0,
        fetchGeneration: 0,
    }),

    getters: {
        hasUnreadNotifications: (state) => state.unreadCount > 0,
    },

    actions: {
        invalidateContext() {
            this.fetchGeneration++;
            this.notifications = [];
            this.unreadCount = 0;
            this.lastFetchedAt = null;
            this.loading = false;
            this.error = false;
            this.announcement = '';
        },
        /** Alias historique : charge le panneau de la cloche. */
        async fetchUnreadNotifications(force = false) {
            return this.fetchRecent(force);
        },
        async fetchRecent(force = false) {
            // Cache de 30 secondes
            if (
                !force &&
                this.lastFetchedAt &&
                new Date().getTime() - this.lastFetchedAt.getTime() < 30000
            ) {
                return;
            }

            const page = usePage();
            const owner = `${page.props.auth?.user?.id ?? ''}:${page.props.app?.household?.id ?? ''}`;
            const ticket = ++this.fetchGeneration;
            const previous = this.lastFetchedAt ? this.unreadCount : null;
            try {
                this.loading = true;
                const data = await json<{
                    notifications: Notification[];
                    unread_count: number;
                }>('get', recentNotificationsRoute.url());
                if (
                    ticket !== this.fetchGeneration ||
                    owner !==
                        `${page.props.auth?.user?.id ?? ''}:${page.props.app?.household?.id ?? ''}`
                )
                    return;
                this.notifications = data.notifications;
                this.unreadCount = data.unread_count;
                this.lastFetchedAt = new Date();
                this.error = false;
                // Annonce polie des arrivées (jamais au premier chargement).
                if (previous !== null && data.unread_count > previous) {
                    const count = data.unread_count - previous;
                    this.announcement = transChoice(
                        'notifications.new_announcement',
                        count,
                        { count: String(count) },
                    );
                    this.announcementKey++;
                }
            } catch (error) {
                if (ticket === this.fetchGeneration) this.error = true;
                console.error('Failed to fetch notifications:', error);
            } finally {
                if (ticket === this.fetchGeneration) this.loading = false;
            }
        },

        /** Marque une notification lue dans le panneau (elle y reste). */
        markLocally(notificationId: string | number, wasUnread: boolean) {
            const readAt = new Date().toISOString();
            this.notifications = this.notifications.map((n) =>
                String(n.id) === String(notificationId) && !n.read_at
                    ? { ...n, read_at: readAt }
                    : n,
            );
            if (wasUnread) this.unreadCount = Math.max(0, this.unreadCount - 1);
        },

        /** Retire une notification supprimée de la cloche. */
        forget(notificationId: string | number, wasUnread: boolean) {
            this.notifications = this.notifications.filter(
                (n) => String(n.id) !== String(notificationId),
            );
            if (wasUnread) this.unreadCount = Math.max(0, this.unreadCount - 1);
        },

        /**
         * Idempotent : une notification déjà lue dans le panneau n'est pas
         * renvoyée, un marquage en cours est partagé, et le compteur ne baisse
         * que pour une notification encore non lue localement.
         */
        async markAsRead(notificationId: string | number): Promise<boolean> {
            const key = String(notificationId);
            const pending = pendingReads.get(key);
            if (pending) return pending;
            const isReadLocally = () =>
                Boolean(
                    this.notifications.find((n) => String(n.id) === key)
                        ?.read_at,
                );
            if (isReadLocally()) return true;

            const request = (async () => {
                try {
                    await json('post', markAsReadRoute.url(key));
                    this.markLocally(notificationId, !isReadLocally());
                    return true;
                } catch (error) {
                    showMarkError(error);
                    return false;
                } finally {
                    pendingReads.delete(key);
                }
            })();
            pendingReads.set(key, request);
            return request;
        },

        async markAllAsRead(): Promise<boolean> {
            try {
                await json('post', markAllAsReadRoute.url());
                const readAt = new Date().toISOString();
                this.notifications = this.notifications.map((n) =>
                    n.read_at ? n : { ...n, read_at: readAt },
                );
                this.unreadCount = 0;
                return true;
            } catch (error) {
                showMarkError(error);
                return false;
            }
        },

        /** Supprime une notification ; `wasUnread` tient le compteur juste. */
        async destroy(
            notificationId: string | number,
            wasUnread: boolean,
        ): Promise<boolean> {
            try {
                await json('delete', destroyRoute.url(String(notificationId)));
                this.forget(notificationId, wasUnread);
                return true;
            } catch (error) {
                showMarkError(error);
                return false;
            }
        },

        goToNotifications() {
            router.visit(indexNotificationsRoute.url());
        },
    },
});
